const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * How many orders will be read one at a time before giving up.
 *
 * `first_amount` is on the single-order endpoint but not on the list, so it
 * has to be fetched per order until the server sends it with the list.
 * Recovering it for a handful is worth a few requests; for hundreds it is
 * not, and the columns show "-" rather than hammering the server.
 */
export const DETAIL_CAP = 250;

/** Resolve n promises at a time rather than all at once. */
async function inBatches(items, size, fn) {
  const out = [];

  for (let i = 0; i < items.length; i += size) {
    // eslint-disable-next-line no-await-in-loop
    out.push(...(await Promise.all(items.slice(i, i + size).map(fn))));
  }

  return out;
}

/**
 * first_amount for each of these orders, keyed by order id.
 *
 * Orders that already carry it cost nothing. Returns an empty map when there
 * is nothing to do or when there are too many to be worth it, so a caller can
 * tell "not known" from "zero".
 */
export default async function fetchFirstAmounts(orders) {
  const found = new Map();

  if (!Array.isArray(orders) || orders.length === 0) return found;

  const missing = [];

  orders.forEach((o) => {
    // an order with no id cannot be keyed or looked up; callers read the
    // inline value for those, so recording it here would only let one
    // undefined key overwrite the next
    if (!o.id) return;

    if (o.first_amount !== undefined && o.first_amount !== null) {
      found.set(o.id, Number(o.first_amount));
    } else {
      missing.push(o);
    }
  });

  if (missing.length === 0 || missing.length > DETAIL_CAP) return found;

  const details = await inBatches(missing, 8, (o) =>
    fetch(`${API}/orders/${o.id}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
  );

  details.forEach((d, i) => {
    if (d && d.first_amount !== undefined && d.first_amount !== null) {
      found.set(missing[i].id, Number(d.first_amount));
    }
  });

  return found;
}
